/**
 * Single source of truth for the reports module.
 *
 * The index screen, the router and each report page all read from here, so a
 * report is added or renamed in exactly one place. Grouping and wording match
 * the signed-off wireframe — see fms-wireframe/reports-wireframe.html.
 *
 * Aged Debt is deliberately absent: removed from scope on request, 2026-09-03.
 * It was present in the signed-off wireframe, so the client should be told.
 *
 * REV6 (Franchise Fee Run-Rate) is deliberately absent: verification against the
 * database found 17 contracts across 258 franchises, only 2 with a non-zero
 * monthly fee, so every figure on it renders zero. It is cut until contract data
 * is backfilled. Do not re-add it without checking that first.
 */

export type ReportGroup = 'money' | 'growth' | 'supply' | 'printing';

export interface ReportDef {
  /** Stable identifier used in specs, tickets and the API contract. */
  code: string;
  /** URL segment: /reports/:slug */
  slug: string;
  name: string;
  /** The question the report answers, in the user's words. */
  question: string;
  group: ReportGroup;
  /** 'live' once the report is implemented; 'planned' renders the not-yet state. */
  status: 'live' | 'planned';
}

export const REPORT_GROUPS: {key: ReportGroup; label: string; blurb: string}[] = [
  {
    key: 'money',
    label: 'Money',
    blurb: 'Every report with a currency figure, across all money flows.'
  },
  {
    key: 'growth',
    label: 'Growth',
    blurb: 'Where enquiries come from, where they stall, and how fast they move.'
  },
  {
    key: 'supply',
    label: 'Supply & fulfilment',
    blurb: 'How much was ordered, how fast it shipped, and how much has been used.'
  },
  {
    key: 'printing',
    label: 'Printing',
    blurb: 'What is being printed, by whom, and against which limits.'
  }
];

export const REPORTS: ReportDef[] = [
  // ---- Money -------------------------------------------------------------
  {code: 'REV1', slug: 'materials-revenue', group: 'money', status: 'live',
   name: 'Materials Revenue', question: 'What did franchises spend with head office?'},
  {code: 'REV2', slug: 'student-subject-orders', group: 'money', status: 'live',
   name: 'Student Subject Orders', question: 'What did franchises pay per student, per subject?'},
  {code: 'REV3', slug: 'tuition-revenue', group: 'money', status: 'live',
   name: 'Tuition Revenue', question: 'What did we invoice, and for what?'},
  {code: 'REV4', slug: 'credit-notes', group: 'money', status: 'live',
   name: 'Credit Notes & Adjustments', question: 'What are we writing back, and why?'},

  // ---- Growth ------------------------------------------------------------
  {code: 'LEAD1', slug: 'lead-funnel', group: 'growth', status: 'live',
   name: 'Lead Funnel', question: 'Where do enquiries stall?'},
  {code: 'LEAD2', slug: 'lead-sources', group: 'growth', status: 'live',
   name: 'Source Effectiveness', question: 'Which channels actually convert?'},
  {code: 'LEAD3', slug: 'lead-velocity', group: 'growth', status: 'live',
   name: 'Lead Velocity', question: 'How long does each stage take?'},

  // ---- Supply & fulfilment ----------------------------------------------
  {code: 'ORD1', slug: 'order-volume', group: 'supply', status: 'live',
   name: 'Order Volume & Fulfilment', question: 'How much are we shipping, how fast?'},
  {code: 'ORD2', slug: 'item-demand', group: 'supply', status: 'live',
   name: 'Item Demand', question: 'Which sets, blocks and modules move?'},
  {code: 'ORD3', slug: 'student-work-orders', group: 'supply', status: 'live',
   name: 'Student Work Orders', question: 'What is pending, and how used-up are sets?'},

  // ---- Printing ----------------------------------------------------------
  {code: 'PRT1', slug: 'print-volume', group: 'printing', status: 'live',
   name: 'Print Volume & Mix', question: 'How much are we printing, of what kind?'},
  {code: 'PRT2', slug: 'adhoc-prints', group: 'printing', status: 'live',
   name: 'Adhoc Print Usage', question: 'Who prints off-plan, and how much?'},
  {code: 'PRT3', slug: 'threshold-breaches', group: 'printing', status: 'live',
   name: 'Threshold Breaches', question: 'Who exceeded their print cap?'}
];

export const findReportBySlug = (slug?: string): ReportDef | undefined =>
  REPORTS.find((r) => r.slug === slug);

export const reportsInGroup = (group: ReportGroup): ReportDef[] =>
  REPORTS.filter((r) => r.group === group);
